import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, Wallet, X } from "lucide-react";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { TourCard } from "@/components/india/TourCard";
import { TOURS } from "@/data/tours";
import { searchTours, isExactMatch, sortTours, parseBudget, describeBudget, getDeal } from "@/lib/search";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = ["Relevance", "Price: Low to High", "Price: High to Low", "Duration: Short to Long", "Duration: Long to Short"];
const SCOPES = [
  { key: "all", label: "All" },
  { key: "india", label: "India" },
  { key: "world", label: "World" },
];
const POPULAR = ["Kashmir", "Kerala", "Rajasthan", "Andaman", "Europe", "Switzerland", "Dubai", "Thailand", "Bali", "Japan"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [scope, setScope] = useState("all");
  const [sort, setSort] = useState("Relevance");
  const budget = useMemo(() => parseBudget(q), [q]);

  const pool = useMemo(() => (scope === "all" ? TOURS : TOURS.filter((t) => t.scope === scope)), [scope]);
  const exact = useMemo(() => isExactMatch(pool, q), [pool, q]);
  const dealsOnly = params.get("deals") === "1";
  const results = useMemo(() => {
    const base = q ? searchTours(pool, q) : pool;
    return sortTours(dealsOnly ? base.filter(getDeal) : base, sort);
  }, [pool, q, sort, dealsOnly]);

  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden" data-testid="search-page">
      <Header />
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8">
          <nav className="text-[12px] text-gray-500 mb-4">
            <Link to="/" className="hover:text-[var(--navy)]">Home</Link>
            <span className="mx-1.5">&gt;</span>
            <span className="text-[var(--navy)] font-medium">Search</span>
          </nav>
          <h1 className="font-serif-display text-3xl md:text-[42px] leading-tight" data-testid="search-title">
            {budget.text ? <>Results for <span className="text-[var(--yellow-2)]">“{budget.text}”</span></> : budget.label ? "Tours in your budget" : dealsOnly ? "Unbeatable Deals" : "All Tour Packages"}
          </h1>
          {budget.label && (
            <button type="button" onClick={() => setParams(budget.text ? { q: budget.text } : {})} data-testid="search-budget-chip" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--navy)] text-white text-[12.5px] font-semibold pl-3 pr-2.5 py-1.5 hover:bg-[var(--red)] transition-colors">
              <Wallet className="w-3.5 h-3.5" /> Budget: {describeBudget(budget)} <X className="w-3.5 h-3.5" />
            </button>
          )}
          {q && !exact && !(budget.label && results.length === 0) && (
            <p className="mt-3 text-[14px] text-gray-600 bg-[#fff8e7] border border-[#f59e0b]/40 rounded-lg px-4 py-2.5 inline-block" data-testid="search-no-exact">
              {results.length > 0 ? `We don't have a package for “${q}” yet. Here are the closest tours.` : `We don't have any packages for “${q}” yet.`}{" "}
              <Link to="/contact" className="font-semibold underline">Ask our experts for a custom trip</Link>
            </p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="popular-searches">
            <span className="text-[12px] text-gray-500 mr-1">Try:</span>
            {["Kerala under 40000", "Europe above 2 lakh", "Dubai 50k-1L"].map((p) => (
              <button key={p} type="button" onClick={() => setParams({ q: p })} data-testid={`budget-example-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-[#f59e0b]/50 bg-[#fff8e7] px-3 py-1 text-[12.5px] hover:border-[var(--navy)] transition">
                {p}
              </button>
            ))}
            <span className="w-full h-0" />
            <span className="text-[12px] text-gray-500 mr-1">Popular:</span>
            {POPULAR.map((p) => (
              <button key={p} type="button" onClick={() => setParams({ q: p })} data-testid={`popular-${p.toLowerCase()}`} className={`rounded-full border px-3 py-1 text-[12.5px] transition ${p === q ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-gray-200 hover:border-[var(--navy)]"}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--soft)] py-8">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="font-bold text-[17px]" data-testid="search-results-count">{results.length} {results.length === 1 ? "package" : "packages"} found</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex bg-white border border-gray-200 rounded-full p-1" data-testid="search-scope">
                {SCOPES.map((s) => (
                  <button key={s.key} type="button" onClick={() => setScope(s.key)} data-testid={`search-scope-${s.key}`} className={`px-4 py-1.5 rounded-full text-[13px] font-semibold transition ${scope === s.key ? "bg-[var(--navy)] text-white" : "text-[#0b2545]/70 hover:text-[var(--navy)]"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[190px] h-9 text-[13px] font-semibold bg-white" data-testid="search-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORTS.map((o) => <SelectItem key={o} value={o} className="text-[13px]">{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {results.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-16 text-center text-gray-500" data-testid="search-empty">
              <SearchIcon className="w-8 h-8 mx-auto text-[var(--yellow-2)] mb-3" />
              {budget.label ? (
                <>
                  No {budget.text ? `“${budget.text}” ` : ""}tours in your budget ({describeBudget(budget)}).{" "}
                  <button type="button" onClick={() => setParams(budget.text ? { q: budget.text } : {})} data-testid="search-empty-remove-budget" className="underline font-semibold text-[var(--navy)]">Try without the budget</button> or <Link to="/contact" className="underline font-semibold text-[var(--navy)]">talk to an expert</Link>.
                </>
              ) : (
                <>No packages match “{q}”. Try one of the popular searches above or <Link to="/contact" className="underline font-semibold text-[var(--navy)]">talk to an expert</Link>.</>
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-results">
              {results.map((t, i) => <TourCard key={t.slug} tour={t} view="grid" index={i} />)}
            </div>
          )}
        </div>
      </section>
      <Footer />
      <FloatingWidgets />
    </div>
  );
}
